import React, { FC, ReactNode } from 'react';
import { DocumentNode } from 'graphql';
import { createStyles, withStyles, useTheme, Theme, WithStyles } from '@material-ui/core/styles';
import {
  ListItem,
  Link,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from '@material-ui/core';

import { FeedKey, PublicUserProfile } from 'api';
import { getFullName } from 'helpers';
import { FollowToggle, ListWidget, ToggleButton, UserAvatar } from '../';
import { generateLink } from 'Links';

interface IProps extends WithStyles<typeof styles> {
  title: string | ReactNode;
  results: PublicUserProfile[];
  maxResults: number;
  maxWidth: number;
  refetchQueries?: Array<{ query: DocumentNode; variables?: object }>;
  link?: string;
}

const styles = (theme: Theme) =>
  createStyles({
    secondaryAction: {
      paddingRight: 0,
    },
    listItemText: {
      paddingLeft: theme.spacing(1),
      paddingRight: 0,
      maxWidth: 180,
    },
    jobTitle: {
      fontSize: theme.typography.pxToRem(12),
    },
  });

const ProfilesWidget: FC<IProps> = props => {
  const { classes, results, maxResults, maxWidth, refetchQueries, title, link } = props;
  const theme = useTheme();

  const style = {
    ...theme.mixins.overflowText(maxWidth),
  };

  function renderProfile(profile: PublicUserProfile) {
    if (!profile) {
      return null;
    }

    return (
      <ListItem
        key={profile.id}
        disableGutters
        classes={{ secondaryAction: classes.secondaryAction }}>
        <UserAvatar link user={profile} size={30} />
        <ListItemText
          style={style}
          className={classes.listItemText}
          disableTypography
          primary={
            <Link
              title={getFullName(profile)}
              variant="body2"
              component={generateLink(`/profile/${profile.id}`)}
              noWrap>
              {getFullName(profile)}
            </Link>
          }
          secondary={
            <Typography
              title={profile.job_title}
              color="textSecondary"
              className={classes.jobTitle}
              noWrap>
              {profile.job_title}
            </Typography>
          }
        />
        <ListItemSecondaryAction>
          <FollowToggle source={profile} type={FeedKey.User} refetchQueries={refetchQueries}>
            {({ isFollowing, onClick, disableToggle, loading }) =>
              !disableToggle && (
                <ToggleButton
                  size={20}
                  disabled={loading}
                  selected={isFollowing}
                  onClick={onClick}
                />
              )
            }
          </FollowToggle>
        </ListItemSecondaryAction>
      </ListItem>
    );
  }

  return (
    <ListWidget color="primary" title={title} link={link}>
      {results.slice(0, maxResults).map(result => renderProfile(result))}
    </ListWidget>
  );
};

export default withStyles(styles)(ProfilesWidget);
